import { Card, Col, Form, Input, InputNumber, Row, Select, Table } from "antd";
import CustomDatePicker from "common/CustomDatePicker";
import { DailyFinishingLinePlanContext } from "context/DailyFinishingLinePlanContext";
import { useContext, useState } from "react";
import { useParams } from "react-router";
import "./index.css";

export default function DailyLinePlanForm() {
  const {
    form,
    onFinish,
    options,
    onChangeStyle,
    onChangeTypeOrLine,
    onChangeSupervisorId,
    onSearchStyle,
    onSearchSize,
    onSearchSupervisor,
    onSearchOperator,
    onSearchPO,
  } = useContext(DailyFinishingLinePlanContext);
  const { id } = useParams();

  // Operator dropdown enabled only if type, line, and supervisor are selected
  const isOperatorEnabled = () => {
    const type = form.getFieldValue("type");
    const finishingLineId = form.getFieldValue("finishing_line_id");
    const supervisorId = form.getFieldValue("supervisor_id");
    return !!(type && finishingLineId && supervisorId);
  };

  // Track if both type and line are selected
  const [isSupervisorEnabled, setIsSupervisorEnabled] = useState(false);

  // Check if both type and line are selected
  const checkSupervisorEnabled = () => {
    const type = form.getFieldValue("type");
    const finishingLineId = form.getFieldValue("finishing_line_id");
    // Always reset supervisor field on type or line change
    form.setFieldsValue({ supervisor_id: undefined, operations: [] });
    const enabled = !!(type && finishingLineId);
    setIsSupervisorEnabled(enabled);
    if (enabled && onChangeTypeOrLine) {
      onChangeTypeOrLine(type, finishingLineId);
      onChangeSupervisorId(undefined); // Reset supervisor when type or line changes
    }
  };

  // Handle operator selection to auto-populate name
  const handleOperatorChange = (value, index) => {
    const selectedOperator = options.operators?.find(
      (op) => op.value === value,
    );
    if (selectedOperator) {
      // Get current operations array
      const operations = form.getFieldValue("operations") || [];
      // Update the operator_id and operator_name for the selected row
      const updated = operations.map((item, idx) =>
        idx === index
          ? {
              ...item,
              operator_id: value,
              operator_name: selectedOperator.label.split(" - ")[1],
            }
          : item,
      );
      form.setFieldsValue({
        operations: updated,
      });
    }
  };

  return (
    <Card>
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <br />
        <Row gutter={24} align={"middle"}>
          <Col span={4}>
            <Form.Item
              label="Date"
              name="plan_date"
              rules={[{ required: true, message: "Please select Date!" }]}
              initialValue={new Date()} // Set current date as default
            >
              <CustomDatePicker
                showTime
                displayFormat="DD-MMM-YYYY hh:mm A"
                placeholder="Select Date"
                style={{ width: "100%" }}
                size="medium"
              />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item
              label="Type"
              name="type"
              rules={[{ required: true, message: "Please select Type!" }]}
            >
              <Select
                placeholder="Select Type"
                onChange={checkSupervisorEnabled}
              >
                {["Iron", "Folding", "Poly"].map((line) => (
                  <Select.Option key={line} value={line}>
                    {line}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Line"
              name="finishing_line_id"
              rules={[{ required: true, message: "Please select Line!" }]}
            >
              <Select
                placeholder="Select Line"
                showSearch
                optionFilterProp="children"
                // onSearch={onSearchLine}
                onChange={checkSupervisorEnabled}
              >
                {options.finishingLines?.map((line) => (
                  <Select.Option key={line.value} value={line.value}>
                    {line.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Supervisor"
              name="supervisor_id"
              rules={[{ required: true, message: "Please select Supervisor!" }]}
            >
              <Select
                placeholder="Select Supervisor"
                showSearch
                optionFilterProp="children"
                onSearch={onSearchSupervisor}
                disabled={id ? false : !isSupervisorEnabled}
                onChange={onChangeSupervisorId}
              >
                {options?.supervisors?.map((supervisor) => (
                  <Select.Option
                    key={supervisor.value}
                    value={supervisor.value}
                  >
                    {supervisor.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Card
              style={{
                backgroundColor: "#f6f8fa",
                marginTop: "20px",
                // marginLeft: "20px",
                // marginRight: "20px",
              }}
            >
              <Row gutter={24} align={"middle"}>
                <Col span={4}>
                  <Form.Item
                    label="Style"
                    name="style_id"
                    rules={[
                      { required: true, message: "Please select Style!" },
                    ]}
                  >
                    <Select
                      placeholder="Select Style"
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      onSearch={onSearchStyle}
                      onChange={onChangeStyle}
                    >
                      {options?.styles?.map((style) => (
                        <Select.Option key={style.value} value={style.value}>
                          {style.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Sub-Style" name="sub_style_id">
                    <Select
                      placeholder="Select Sub-Style"
                      showSearch
                      optionFilterProp="children"
                    >
                      {options?.subStyles?.map((style) => (
                        <Select.Option key={style.id} value={style.id}>
                          {style.sub_style_no}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    label="PO"
                    name="po_map_id"
                    rules={[{ required: false, message: "Please select PO!" }]}
                  >
                    <Select
                      placeholder="Select PO"
                      showSearch
                      optionFilterProp="children"
                      onSearch={onSearchPO}
                    >
                      {options?.po?.map((po) => (
                        <Select.Option key={po.value} value={po.value}>
                          {po.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item label="Color" name="color_id">
                    <Select
                      disabled
                      placeholder="Select Color"
                      showSearch
                      optionFilterProp="children"
                    >
                      {options?.colors?.map((color) => (
                        <Select.Option
                          key={color.color_id}
                          value={color.color_id}
                        >
                          {color.color_info?.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item label="Size" name="size_id">
                    <Select
                      disabled
                      placeholder="Select Size"
                      showSearch
                      optionFilterProp="children"
                      onSearch={onSearchSize}
                    >
                      {options?.sizes?.map((size) => (
                        <Select.Option key={size.value} value={size.value}>
                          {size.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item name="sales_contract_id" noStyle>
                    <Input hidden />
                  </Form.Item>
                  <Form.Item label="Sales Contract" name="sales_contract">
                    <Input
                      disabled
                      placeholder="Auto populated from Style"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                {form.getFieldValue("buyer_id") && (
                  <Col span={4}>
                    <Form.Item name="buyer_id" noStyle>
                      <Input hidden />
                    </Form.Item>
                    <Form.Item label="Buyer" name="buyer">
                      <Input placeholder="Auto populated from Style" disabled />
                    </Form.Item>
                  </Col>
                )}
                {form.getFieldValue("buying_office_id") && (
                  <Col span={4}>
                    <Form.Item name="buying_office_id" noStyle>
                      <Input hidden />
                    </Form.Item>
                    <Form.Item label="Buying Office" name="buying_office">
                      <Input placeholder="Auto populated from Style" disabled />
                    </Form.Item>
                  </Col>
                )}
                {form.getFieldValue("sub_contract_factory_id") && (
                  <Col span={4}>
                    <Form.Item name="sub_contract_factory_id" noStyle>
                      <Input hidden />
                    </Form.Item>
                    <Form.Item label="Factory" name="factory">
                      <Input placeholder="Auto populated from Style" disabled />
                    </Form.Item>
                  </Col>
                )}
                <Col span={24} className="DailyLinePlanList-table-responsive">
                  <Form.List name="operations" initialValue={[]}>
                    {(fields, { add, remove }) => (
                      <>
                        <Col span={24}>
                          <div
                            style={{ marginTop: "20px", marginBottom: "10px" }}
                          >
                            <h4>Operation Details (Repetitive Table Data)</h4>
                          </div>
                        </Col>
                        <Col
                          span={24}
                          className="DailyLinePlanList-table-responsive"
                        >
                          <Table
                            dataSource={fields}
                            pagination={false}
                            bordered
                            size="middle"
                            rowKey="key"
                            columns={[
                              {
                                title: "Operator Card No",
                                dataIndex: "operator_id",
                                key: "operator_id",
                                render: (text, record, index) => (
                                  <Form.Item
                                    name={[record.name, "operator_id"]}
                                    style={{ margin: 0 }}
                                  >
                                    <Select
                                      disabled
                                      placeholder="Select Operator"
                                      showSearch
                                      optionFilterProp="children"
                                      style={{ width: "100%" }}
                                      onSearch={(value) => {
                                        if (isOperatorEnabled()) {
                                          onSearchOperator(value);
                                        }
                                      }}
                                      onChange={(value) =>
                                        handleOperatorChange(value, record.name)
                                      }
                                    >
                                      {options?.operators?.map((operator) => (
                                        <Select.Option
                                          key={operator.value}
                                          value={operator.value}
                                        >
                                          {operator.label}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                ),
                              },
                              {
                                title: "Operator Name",
                                dataIndex: "operator_name",
                                key: "operator_name",
                                render: (text, record) => (
                                  <Form.Item
                                    name={[record.name, "operator_name"]}
                                    style={{ margin: 0 }}
                                  >
                                    <Input
                                      placeholder="Auto populated"
                                      disabled
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                ),
                              },
                              {
                                title: "Hourly Target",
                                dataIndex: "hourly_target",
                                key: "hourly_target",
                                render: (text, record) => (
                                  <Form.Item
                                    name={[record.name, "hourly_target"]}
                                    style={{ margin: 0 }}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Enter Target",
                                      },
                                    ]}
                                  >
                                    <InputNumber
                                      placeholder="Enter Target"
                                      style={{ width: "100%" }}
                                      min={0}
                                    />
                                  </Form.Item>
                                ),
                              },
                              // {
                              //   title: "Action",
                              //   key: "action",
                              //   render: (text, record, index) => (
                              //     <Space>
                              //       {fields.length > 1 && (
                              //         <Button
                              //           type="primary"
                              //           danger
                              //           icon={<MinusCircleOutlined />}
                              //           onClick={() => remove(record.name)}
                              //           title="Remove Row"
                              //           size="middle"
                              //         />
                              //       )}
                              //     </Space>
                              //   ),
                              // },
                            ]}
                          />
                        </Col>
                      </>
                    )}
                  </Form.List>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Form>
    </Card>
  );
}
